// HUD manages all DOM overlays: position badge, item slot, speed, progress
// bar, countdown, trick popup, lobby screen, results screen. update() is
// called every frame by GameLoop with the current GameState.
export class HUD {
  constructor() {
    this.elements = {
      positionPlace: document.getElementById('position-place'),
      positionTotal: document.getElementById('position-total'),
      cupProgress: document.getElementById('cup-progress'),
      itemSlot: document.getElementById('item-slot'),
      itemIcon: document.getElementById('item-icon'),
      itemFireHint: document.getElementById('item-fire-hint'),
      speedValue: document.getElementById('speed-value'),
      tuckIndicator: document.getElementById('tuck-indicator'),
      progressFill: document.getElementById('progress-fill'),
      countdown: document.getElementById('countdown'),
      countdownSmoke: document.getElementById('countdown-smoke'),
      trickPopup: document.getElementById('trick-popup'),
      finishBanner: document.getElementById('finish-banner'),
      finishSubtext: document.getElementById('finish-subtext'),
      intermission: document.getElementById('intermission'),
      intermissionTitle: document.getElementById('intermission-title'),
      intermissionStandings: document.getElementById('intermission-standings'),
      intermissionTimer: document.getElementById('intermission-timer'),
      lobbyScreen: document.getElementById('lobby-screen'),
      resultsScreen: document.getElementById('results-screen'),
      standings: document.getElementById('standings'),
      connectionStatus: document.getElementById('connection-status'),
      joinButton: document.getElementById('join-queue-button'),
      queueStatus: document.getElementById('queue-status'),
      startNowButton: document.getElementById('start-now-button'),
      startOverButton: document.getElementById('results-start-over-button'),
      liveGamesList: document.getElementById('live-games-list'),
      liveGamesEmpty: document.getElementById('live-games-empty'),
      leaderboard: document.getElementById('leaderboard'),
      leaderboardTop3: document.getElementById('leaderboard-top3'),
      leaderboardViewMore: document.getElementById('leaderboard-view-more'),
      leaderboardEmpty: document.getElementById('leaderboard-empty'),
      leaderboardBody: document.getElementById('leaderboard-body'),
      totalMatches: document.getElementById('total-matches'),
      spectatorBar: document.getElementById('spectator-bar'),
      spectatorTarget: document.getElementById('spectator-target'),
      stopSpectatingButton: document.getElementById('stop-spectating-button'),
      aiButton: document.getElementById('play-vs-ai-button'),
      aiDifficulty: document.getElementById('ai-difficulty'),
    };
    this._setupModals();
  }

  // Wires the lobby's footer + Leaderboard buttons to their modals. Each
  // modal closes on backdrop click, on its Close button (data-close-modal),
  // and on Escape while open. Idempotent — called once from the
  // constructor.
  _setupModals() {
    const bind = (triggerId, modalId) => {
      const trigger = document.getElementById(triggerId);
      const modal = document.getElementById(modalId);
      if (!trigger || !modal) return;
      trigger.addEventListener('click', () => modal.classList.remove('hidden'));
    };
    bind('about-button', 'about-modal');
    bind('how-built-button', 'how-built-modal');
    bind('credits-button', 'credits-modal');
    bind('leaderboard-view-more', 'leaderboard-modal');

    document.querySelectorAll('.modal').forEach((modal) => {
      const close = () => modal.classList.add('hidden');
      modal.querySelector('.modal-backdrop')?.addEventListener('click', close);
      modal.querySelectorAll('[data-close-modal]').forEach((btn) => {
        btn.addEventListener('click', close);
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('.modal:not(.hidden)').forEach((m) => {
        m.classList.add('hidden');
      });
    });
  }

  setConnected(connected) {
    if (this.elements.connectionStatus) {
      this.elements.connectionStatus.textContent = connected ? 'Connected' : 'Connecting...';
    }
    if (this.elements.joinButton) this.elements.joinButton.disabled = !connected;
    if (this.elements.aiButton) this.elements.aiButton.disabled = !connected;
  }

  getSelectedDifficulty() {
    return this.elements.aiDifficulty?.value || 'medium';
  }

  // Resolves the chosen track select into a { mode, trackId } pair. The
  // dropdown values are 'cup' or 'single:<id>' so a single helper handles
  // both lobby panels.
  getMultiplayerSelection() { return this._parseTrackSelect('mp-track'); }
  getSingleplayerSelection() { return this._parseTrackSelect('ai-track'); }
  _parseTrackSelect(id) {
    const el = document.getElementById(id);
    const raw = el?.value || 'cup';
    if (raw.startsWith('single:')) {
      return { mode: 'single', trackId: raw.slice('single:'.length) };
    }
    return { mode: 'cup', trackId: '' };
  }

  onPlayVsAIClicked(handler) {
    this.elements.aiButton?.addEventListener('click', handler);
  }

  showWaitingForRacers(waiting) {
    this.elements.queueStatus?.classList.toggle('hidden', !waiting);
    this.elements.startNowButton?.classList.toggle('hidden', !waiting);
    if (this.elements.joinButton) {
      this.elements.joinButton.disabled = waiting;
    }
  }

  onStartNowClicked(handler) {
    this.elements.startNowButton?.addEventListener('click', handler);
  }

  onJoinClicked(handler) {
    this.elements.joinButton?.addEventListener('click', handler);
  }
  onStartOverClicked(handler) {
    this.elements.startOverButton?.addEventListener('click', handler);
  }

  onStopSpectatingClicked(handler) {
    this.elements.stopSpectatingButton?.addEventListener('click', handler);
  }

  // Re-renders the lobby's Live Games list from a server-supplied array.
  // Each item gets a "Watch" button wired via the provided onWatch callback.
  renderLiveGames(games, onWatch) {
    const list = this.elements.liveGamesList;
    const empty = this.elements.liveGamesEmpty;
    if (!list || !empty) return;
    list.innerHTML = '';
    if (!games || games.length === 0) {
      empty.classList.remove('hidden');
      list.classList.add('hidden');
      return;
    }
    empty.classList.add('hidden');
    list.classList.remove('hidden');
    for (const g of games) {
      const li = document.createElement('li');
      const track = document.createElement('span');
      track.className = 'live-game-track';
      const cup = g.cupTotalRaces > 1 ? ` (race ${g.cupRaceIndex}/${g.cupTotalRaces})` : '';
      track.textContent = `${g.trackName || '?'}${cup}`;
      li.appendChild(track);
      const racers = document.createElement('span');
      racers.className = 'live-game-racers';
      racers.textContent = (g.racerNames || []).join(' · ');
      li.appendChild(racers);
      const btn = document.createElement('button');
      btn.className = 'watch-button';
      btn.textContent = 'Watch';
      btn.addEventListener('click', () => onWatch?.(g.gameId));
      li.appendChild(btn);
      list.appendChild(li);
    }
  }

  // Re-renders the Top Riders block from a server-pushed payload. The
  // server only sends this when LEADERBOARD_PATH is configured, so its
  // arrival is also the signal to un-hide the whole block. We render the
  // top 3 inline as a teaser and stuff the full list into the modal's
  // table, which only opens when the user clicks "View Full Leaderboard".
  renderLeaderboard(payload) {
    const block = this.elements.leaderboard;
    const top3 = this.elements.leaderboardTop3;
    const viewMore = this.elements.leaderboardViewMore;
    const body = this.elements.leaderboardBody;
    const empty = this.elements.leaderboardEmpty;
    const total = this.elements.totalMatches;
    if (!block || !top3) return;
    block.classList.remove('hidden');

    const entries = payload?.entries || [];
    top3.innerHTML = '';
    if (body) body.innerHTML = '';

    if (entries.length === 0) {
      empty?.classList.remove('hidden');
      viewMore?.classList.add('hidden');
    } else {
      empty?.classList.add('hidden');
      const teaser = entries.slice(0, 3);
      for (let i = 0; i < teaser.length; i++) {
        const e = teaser[i];
        const li = document.createElement('li');
        const rank = document.createElement('span');
        rank.className = 'lb-rank';
        rank.textContent = `${i + 1}`;
        const name = document.createElement('span');
        name.className = 'lb-name';
        name.textContent = e.name;
        const wins = document.createElement('span');
        wins.className = 'lb-stat';
        wins.textContent = `${e.wins}W`;
        const races = document.createElement('span');
        races.className = 'lb-stat';
        races.textContent = `${e.races}R`;
        li.appendChild(rank);
        li.appendChild(name);
        li.appendChild(wins);
        li.appendChild(races);
        top3.appendChild(li);
      }

      // Modal table holds the full leaderboard regardless of how many
      // entries — server already trims to its top-N before pushing.
      if (body) {
        for (let i = 0; i < entries.length; i++) {
          const e = entries[i];
          const tr = document.createElement('tr');
          const rankCell = document.createElement('td');
          rankCell.className = 'rank-cell';
          rankCell.textContent = `${i + 1}`;
          const nameCell = document.createElement('td');
          nameCell.textContent = e.name;
          const winsCell = document.createElement('td');
          winsCell.textContent = `${e.wins}`;
          const racesCell = document.createElement('td');
          racesCell.textContent = `${e.races}`;
          tr.appendChild(rankCell);
          tr.appendChild(nameCell);
          tr.appendChild(winsCell);
          tr.appendChild(racesCell);
          body.appendChild(tr);
        }
      }

      // Only surface the modal trigger when there are more entries than
      // the inline teaser shows.
      viewMore?.classList.toggle('hidden', entries.length <= 3);
    }

    if (total) {
      const n = payload?.totalMatches || 0;
      total.textContent = n > 0 ? `${n} race${n === 1 ? '' : 's'} played` : '';
      total.classList.toggle('hidden', n === 0);
    }
  }

  // Updates the spectator overlay's "watching <name>" label. Called from
  // GameLoop's spectator branch each frame; cheap because the element's
  // textContent only mutates when the target actually changes.
  setSpectatorTarget(racerId, gameState) {
    const el = this.elements.spectatorTarget;
    if (!el) return;
    const name = gameState?.racers?.find((r) => r.racerId === racerId)?.displayName;
    const next = name ? `· ${name}` : '';
    if (el.textContent !== next) el.textContent = next;
  }

  update(gameState) {
    const phase = gameState.phase;

    this.elements.lobbyScreen?.classList.toggle('hidden', phase !== 'lobby');
    this.elements.resultsScreen?.classList.toggle('hidden', phase !== 'finished');
    // SPECTATING overlay while watching someone else's race.
    this.elements.spectatorBar?.classList.toggle('hidden',
      !(gameState.isSpectator && phase !== 'lobby'));

    // Body class for "the player is actively racing" — drives visibility of
    // the in-game HUD chrome and mobile touch controls. Drops as soon as the
    // local racer crosses the finish line so the controls hide while the
    // user is waiting for the remaining racers to finish, even though the
    // server-side phase is still 'racing'.
    const inRace = (phase === 'racing' || phase === 'countdown')
      && !gameState.local?.finished;
    document.body.classList.toggle('in-race', inRace);
    document.body.classList.toggle('spectating', !!gameState.isSpectator);

    if (phase === 'racing' || phase === 'countdown') {
      this._updateRaceHud(gameState);
    }

    // The countdown reads its time from gameState.countdownEnds, not from
    // the phase, so the "GO!" + smoke can keep rendering for ~700ms after
    // the server flips us into racing.
    this._updateCountdown(gameState);

    // FINISH! banner shows the moment the local racer crosses the line and
    // stays until the server sends race_over (phase becomes 'finished' which
    // shows the standings screen). A small subline reminds the player we're
    // waiting on the remaining racers.
    const showFinish = gameState.local?.finished && phase === 'racing';
    this.elements.finishBanner?.classList.toggle('hidden', !showFinish);
    this.elements.finishSubtext?.classList.toggle('hidden', !showFinish);

    // Inter-race leaderboard during the countdown to cup races 2+.
    this._updateIntermission(gameState);

    if (phase === 'finished') {
      this._updateResults(gameState);
    }
  }

  _updateRaceHud(gameState) {
    // Position badge.
    const place = gameState.myPlace();
    const total = gameState.racerCount();
    if (this.elements.positionPlace) {
      this.elements.positionPlace.textContent = place ? ordinal(place) : '--';
    }
    if (this.elements.positionTotal) {
      this.elements.positionTotal.textContent = total ? `/${total}` : '/-';
    }

    // Cup progress caption "RACE 1/3" — shown when the server reports a
    // multi-race cup, hidden during single-race fallbacks.
    if (this.elements.cupProgress) {
      const idx = gameState.cupRaceIndex;
      const total = gameState.cupTotalRaces;
      if (idx > 0 && total > 1) {
        this.elements.cupProgress.textContent = `RACE ${idx}/${total}`;
        this.elements.cupProgress.classList.remove('hidden');
      } else {
        this.elements.cupProgress.classList.add('hidden');
      }
    }

    // Item slot — fade when empty so it doesn't draw the eye.
    if (this.elements.itemIcon) {
      this.elements.itemIcon.textContent = itemIcon(gameState.heldItem);
    }
    if (this.elements.itemSlot) {
      this.elements.itemSlot.classList.toggle('empty', !gameState.heldItem);
    }
    // "PRESS E" hint under the slot while holding something. Hidden on
    // mobile via CSS since touch users have a dedicated Action button.
    if (this.elements.itemFireHint) {
      this.elements.itemFireHint.classList.toggle('hidden', !gameState.heldItem);
    }

    // Speed (km/h-ish, where one game unit ≈ 1 m).
    const v = gameState.local.velocity;
    const speedMS = Math.sqrt(v.x * v.x + v.z * v.z);
    if (this.elements.speedValue) {
      this.elements.speedValue.textContent = Math.round(speedMS * 3.6);
    }
    this.elements.tuckIndicator?.classList.toggle('hidden', !gameState.local.tucking);

    // Progress along track. Use Z distance to finish line as a coarse measure.
    if (gameState.track?.finishLine && this.elements.progressFill) {
      const startZ = gameState.track.startPositions?.[0]?.z ?? 0;
      const finishZ = gameState.track.finishLine.center.z;
      const span = startZ - finishZ;
      const travelled = startZ - gameState.local.position.z;
      const pct = Math.max(0, Math.min(100, (travelled / span) * 100));
      this.elements.progressFill.style.width = `${pct}%`;
    }
  }

  // Shows the previous race's standings during the countdown of cup
  // races 2+, with a "NEXT RACE IN N" caption ticking down. Hidden once
  // the countdown ends or for race 1 / non-cup races.
  _updateIntermission(gameState) {
    const el = this.elements.intermission;
    if (!el) return;
    const remainingMs = gameState.countdownEnds - Date.now();
    const haveStandings = gameState.previousStandings?.length > 0;
    const isCountdown = remainingMs > 0;
    if (!haveStandings || !isCountdown) {
      el.classList.add('hidden');
      this._intermissionRaceId = null;
      return;
    }
    el.classList.remove('hidden');

    // Only rebuild the list when the race changes.
    if (this._intermissionRaceId !== gameState.raceId) {
      this._intermissionRaceId = gameState.raceId;
      const completedRaceNum = Math.max(1, (gameState.cupRaceIndex || 1) - 1);
      if (this.elements.intermissionTitle) {
        this.elements.intermissionTitle.textContent = `RACE ${completedRaceNum} RESULTS`;
      }
      if (this.elements.intermissionStandings) {
        this.elements.intermissionStandings.innerHTML = '';
        const sorted = [...gameState.previousStandings].sort(
          (a, b) => (a.place || 99) - (b.place || 99),
        );
        for (const r of sorted) {
          const li = document.createElement('li');
          const racer = gameState.racers.find((x) => x.racerId === r.racerId);
          const name = racer?.displayName ?? r.racerId.slice(0, 6);
          li.textContent = `${r.place || '?'}. ${name}`;
          this.elements.intermissionStandings.appendChild(li);
        }
      }
    }

    if (this.elements.intermissionTimer) {
      const secs = Math.max(1, Math.ceil(remainingMs / 1000));
      this.elements.intermissionTimer.textContent = `NEXT RACE IN ${secs}`;
    }
  }

  _updateCountdown(gameState) {
    const countdownEnds = gameState.countdownEnds;
    if (!countdownEnds) {
      this._setCountdownStage(null);
      return;
    }
    const remainingMs = countdownEnds - Date.now();
    let stage = null;
    if (remainingMs > 2000) stage = '3';
    else if (remainingMs > 1000) stage = '2';
    else if (remainingMs > 0) stage = '1';
    else if (remainingMs > -700) stage = 'GO!';
    // After GO!'s grace window, hide the countdown.
    this._setCountdownStage(stage);
  }

  // Swap the displayed stage if it changed and restart the pop animation.
  // Triggers a smoke puff when the stage transitions to 'GO!'.
  _setCountdownStage(stage) {
    if (stage === this._countdownStage) return;
    const prev = this._countdownStage;
    this._countdownStage = stage;

    const el = this.elements.countdown;
    if (!el) return;

    if (stage === null) {
      el.classList.add('hidden');
      el.classList.remove('pop');
      return;
    }
    el.textContent = stage;
    el.classList.remove('hidden');
    // Restart the pop animation by removing the class, forcing a reflow,
    // and re-adding it.
    el.classList.remove('pop');
    void el.offsetWidth;
    el.classList.add('pop');

    if (stage === 'GO!' && prev !== 'GO!') {
      this._puffSmoke();
    }
  }

  _puffSmoke() {
    const smoke = this.elements.countdownSmoke;
    if (!smoke) return;
    smoke.classList.remove('hidden');
    smoke.classList.remove('puff');
    void smoke.offsetWidth;
    smoke.classList.add('puff');
  }

  _updateResults(gameState) {
    if (!this.elements.standings) return;
    // Only build the list once per finished race.
    if (this.elements.standings.dataset.raceId === gameState.raceId) return;
    this.elements.standings.dataset.raceId = gameState.raceId;
    this.elements.standings.innerHTML = '';

    const cup = gameState.cupStandings;
    if (cup && cup.length > 0) {
      const sorted = [...cup].sort((a, b) => a.cupPlace - b.cupPlace);
      for (const s of sorted) {
        const li = document.createElement('li');
        const racer = gameState.racers.find((x) => x.racerId === s.racerId);
        const name = racer?.displayName ?? s.racerId.slice(0, 6);
        const races = (s.racePlaces || []).join(' · ');
        li.textContent = `${s.cupPlace}. ${name}  (${races})`;
        this.elements.standings.appendChild(li);
      }
      return;
    }

    // Fallback for non-cup races: show the single-race standings.
    const results = gameState.results || [];
    const sorted = [...results].sort((a, b) => (a.place || 99) - (b.place || 99));
    for (const r of sorted) {
      const li = document.createElement('li');
      const racer = gameState.racers.find((x) => x.racerId === r.racerId);
      const name = racer?.displayName ?? r.racerId.slice(0, 6);
      li.textContent = `${r.place || '?'}. ${name}`;
      this.elements.standings.appendChild(li);
    }
  }
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function itemIcon(type) {
  switch (type) {
    case 'rocket': return '🚀';
    case 'shield': return '🛡';
    case 'tree': return '🎄';
    case 'banana': return '🍌';
    case 'boost': return '⚡';
    default: return '·';
  }
}
